import { useEffect, useState, useCallback } from 'react';
import './App.css';
import Carousel from 'react-bootstrap/Carousel';
import ExampleCarouselImage from './components/ExampleCarouselImage';

const NAV_LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'work', label: 'Work' },
  { id: 'services', label: 'Services' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
];

const SECTION_IDS = [...NAV_LINKS.map((l) => l.id), 'contact'];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState('home');

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Shrink header once the page is scrolled
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Highlight the link for the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Lock page scroll while the mobile menu is open
  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [menuOpen]);

  // Escape closes the menu; resizing up to desktop resets it
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    const onResize = () => window.innerWidth > 820 && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="App">
      <header className={scrolled ? 'site-header scrolled' : 'site-header'}>
        <div className="wrap">
          <nav aria-label="Main navigation">
            <a className="brand" href="#home" aria-label="JB-Solutions home" onClick={closeMenu}>
              <img src="/logo.png" alt="JB-Solutions" className="brand-logo" />
            </a>

            <button
              type="button"
              className={menuOpen ? 'nav-toggle open' : 'nav-toggle'}
              aria-expanded={menuOpen}
              aria-controls="primary-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="bar" />
              <span className="bar" />
              <span className="bar" />
            </button>

            <div
              className={menuOpen ? 'nav-overlay open' : 'nav-overlay'}
              onClick={closeMenu}
              aria-hidden="true"
            />

            <ul id="primary-menu" className={menuOpen ? 'nav-menu open' : 'nav-menu'}>
              {NAV_LINKS.map((link, i) => (
                <li key={link.id} style={{ '--i': i }}>
                  <a
                    href={`#${link.id}`}
                    className={activeId === link.id ? 'active' : undefined}
                    aria-current={activeId === link.id ? 'true' : undefined}
                    onClick={closeMenu}
                  >
                    {link.label}
                  </a>
                </li>
              ))}

            </ul>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero" id="home">
          <div className="wrap">
            <div className="eyebrow">Hull, East Yorkshire</div>
            <h1 style={{ color: 'var(--text)' }}>Creative media. Connected technology.</h1>
            <p>
              JB-Solutions helps local businesses and homes make the most of video,
              smart technology and practical computing.
            </p>
            <a className="button" href="#contact">Discuss a project</a>
          </div>
        </section>

        <section className="showcase" id="work" aria-label="Featured work">
          <div className="wrap">
            <div className="section-heading showcase-heading">
              <div className="eyebrow">Featured work</div>
              <h2 style={{ color: 'var(--text)' }}>Ideas brought to life.</h2>
              <p>A closer look at the creative and technology work from JB-Solutions.</p>
            </div>

            <Carousel
              className="showcase-carousel"
              indicators
              controls
              interval={5000}
              pause="hover"
              touch
              keyboard
              fade
            >
              <Carousel.Item>
                <ExampleCarouselImage
                  src="/images/news.jpg"
                  alt="Featured media project"
                />
                <Carousel.Caption>
                  <h3>Creative media</h3>
                  <p>Visual content with a clear purpose.</p>
                </Carousel.Caption>
              </Carousel.Item>
              <Carousel.Item>
                <ExampleCarouselImage
                  src="/images/smart-home.jpg"
                  alt="Smart home technology project"
                />
                <Carousel.Caption>
                  <h3>Smarter technology</h3>
                  <p>Practical connected solutions for everyday life.</p>
                </Carousel.Caption>
              </Carousel.Item>
              <Carousel.Item>
                <ExampleCarouselImage
                  src="/images/connected-devices.jpg"
                  alt="Connected devices and networking project"
                />
                <Carousel.Caption>
                  <h3>Connected devices</h3>
                  <p>Bringing networks, sensors and devices together.</p>
                </Carousel.Caption>
              </Carousel.Item>
            </Carousel>
          </div>
        </section>

        <section id="services" className="servicesholder">
          <div className="wrap">
            <div className="section-heading">
              <div className="eyebrow">What we do</div>
              <h2>Useful technology, made approachable.</h2>
              <p>From a polished business video to a smarter home setup, JBS brings creative and technical work together.</p>
            </div>

            <div className="services">
              <article className="card">
                <h3>Video production</h3>
                <p>Video content for local businesses, including promotional pieces, social media content and project coverage.</p>
              </article>
              <article className="card">
                <h3>Smart home integration</h3>
                <p>Home Assistant setup and integrations to help bring compatible smart devices together in one place.</p>
              </article>
              <article className="card">
                <h3>Computer technology support</h3>
                <p>Help with smaller technology projects, setup, troubleshooting and making everyday systems work better.</p>
              </article>
              <article className="card">
                <h3>LPWAN &amp; connected devices</h3>
                <p>Exploration and practical support for low-power wide-area networking and connected-device projects.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="about" id="about">
          <div className="wrap about-copy">
            <div className="eyebrow">About JB-Solutions</div>
            <h2>Local service. Thoughtful solutions.</h2>
            <p>
              JB-Solutions is an upcoming Hull-based company working across video production
              and accessible technology. The aim is simple: understand what you need,
              explain the options clearly and build a solution that fits the project.
            </p>
          </div>
        </section>

        <section className="contact" id="contact">
          <div className="wrap contact-box">
            <div>
              <div className="eyebrow">Get in touch</div>
              <h2>Have a project in mind?</h2>
              <p>Get in touch to talk through your video, smart home or technology requirements in Hull and the surrounding area.</p>
            </div>
            <a className="button" href="mailto:jimbob343@yahoo.com">Contact JBS</a>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap">© {new Date().getFullYear()} JB-Solutions · Hull, East Yorkshire</div>
      </footer>
    </div>
  );
}

export default App;
